import { InteractiveObject } from '../display/InteractiveObject';
import { MousePick, PICK_MISS, PICK_PROPAGATE, RenderKind } from '../display/DisplayObject';
import { Event, TextEvent } from '../events/Event';
import { Rectangle } from '../geom/Rectangle';
import { TextFormat } from './TextFormat';
import { Fmt, FormatSpans, applyTextFormat, cloneFmt, defaultFmt, fmtToTextFormat } from './spans';
import { parseHtml, toHtml } from './html';
import { TextLayout, layoutText } from './layout';
import { rt } from '../player/runtime';

/** Runs a string through the host text filter (localization, see Runtime.localize). */
function localized(text: string): string {
  const f = rt().localize;
  return f && text ? f(text) : text;
}
import { TRect, TWIPS, rectContains, rectValid, tmatRect, toTwips } from '../utils/numeric';
import type { EditTextDef } from '../../assets/manifest';
import { measureText as _measureText } from './metrics';

/** Flash TextField gutter: 2px on each side. */
export const GUTTER = 2;

function rangeError(): RangeError {
  return new RangeError('Error #2006: The supplied index is out of bounds.');
}

/**
 * Whether index `i` of `t` falls between the two halves of a surrogate pair (an emoji or another character beyond
 * U+FFFF). PORT: the player's editing keeps such a pair whole (maxChars, Backspace, Delete, the arrows), so an input
 * field never holds a lone surrogate: that is not text, and the backend refuses it (strict JSON decoders of other
 * platforms reject a whole message that carries one).
 */
function insidePair(t: string, i: number): boolean {
  if (i <= 0 || i >= t.length) return false;
  const hi = t.charCodeAt(i - 1);
  const lo = t.charCodeAt(i);
  return hi >= 0xd800 && hi <= 0xdbff && lo >= 0xdc00 && lo <= 0xdfff;
}

/** Parse a Flash `restrict` pattern into a predicate. */
export function compileRestrict(restrict: string | null): ((c: string) => boolean) | null {
  if (restrict === null || restrict === undefined) return null;
  if (restrict === '') return () => false;
  type Item = { lo: number; hi: number; allow: boolean };
  const items: Item[] = [];
  let allow = true;
  let i = 0;
  const chars: Array<{ c: string; escaped: boolean }> = [];
  while (i < restrict.length) {
    const c = restrict[i];
    if (c === '\\' && i + 1 < restrict.length) {
      chars.push({ c: restrict[i + 1], escaped: true });
      i += 2;
    } else {
      chars.push({ c, escaped: false });
      i++;
    }
  }
  let anyAllow = false;
  for (let k = 0; k < chars.length; k++) {
    const { c, escaped } = chars[k];
    if (c === '^' && !escaped) {
      allow = !allow;
      continue;
    }
    if (k + 2 < chars.length && chars[k + 1].c === '-' && !chars[k + 1].escaped) {
      items.push({ lo: c.charCodeAt(0), hi: chars[k + 2].c.charCodeAt(0), allow });
      if (allow) anyAllow = true;
      k += 2;
      continue;
    }
    items.push({ lo: c.charCodeAt(0), hi: c.charCodeAt(0), allow });
    if (allow) anyAllow = true;
  }
  return (ch: string) => {
    const code = ch.charCodeAt(0);
    // Starts with '^': everything allowed except listed; otherwise only listed.
    let ok = !anyAllow;
    for (const it of items) if (code >= it.lo && code <= it.hi) ok = it.allow;
    return ok;
  };
}

/**
 * flash.text.TextField (model + layout; drawing is done by the renderer from `$renderInfo()`).
 */
export class TextField extends InteractiveObject {
  /** @internal */ $spans: FormatSpans;
  /** @internal */ $defaultFmt: Fmt;
  /** @internal local bounds in twips (includes the 2px gutters) */ $bounds: TRect;
  /** @internal */ $type: string;
  /** @internal */ $selectable: boolean;
  /** @internal */ $multiline: boolean;
  /** @internal */ $wordWrap: boolean;
  /** @internal */ $autoSize: string;
  /** @internal */ $border: boolean;
  /** @internal */ $borderColor: number;
  /** @internal */ $background: boolean;
  /** @internal */ $backgroundColor: number;
  /** @internal */ $embedFonts: boolean;
  /** @internal */ $maxChars: number;
  /** @internal */ $restrict: string | null;
  /** @internal */ $restrictFn: ((c: string) => boolean) | null;
  /** @internal */ $password: boolean;
  /**
   * @internal PORT: the field holds a password (displayAsPassword was set), also while its text is shown in clear
   * (a "show password" toggle): its text is measured without the platform's measurement cache and the page's hidden
   * text input stays a password input, so the password is kept nowhere but in this field.
   */
  $sensitive = false;
  /**
   * PORT (touch): the on-screen keyboard's Enter key for this input field (the page's hidden input's enterkeyhint:
   * 'go', 'next', 'done', ...; null: 'go'). A form's first field says "next" when its Enter moves to the next field.
   */
  $enterKeyHint: string | null = null;
  /** PORT: the keyboard layout an input field asks the browser for (the hidden input's inputmode: 'email'); null = default. */
  $inputMode: string | null = null;
  /**
   * PORT (touch): a sibling (its instance name) shown with the field above an on-screen keyboard (BrowserHost's
   * keyboard view): the button in the field's row that its Enter stands for (null: the field alone).
   */
  $keyboardWith: string | null = null;
  /** @internal */ $antiAliasType: string;
  /** @internal */ $condenseWhite: boolean;
  /** @internal */ $scrollV: number;
  /** @internal */ $scrollH: number;
  /** @internal */ $selBegin: number;
  /** @internal */ $selEnd: number;
  /** @internal */ $layout: TextLayout | null;
  /** @internal */ $wasStatic: boolean;
  /** @internal */ $htmlFlag: boolean;
  /** @internal */ $variable: string | null;
  mouseWheelEnabled: boolean;
  alwaysShowSelection: boolean;
  gridFitType: string;
  sharpness: number;
  thickness: number;
  useRichTextClipboard: boolean;
  styleSheet: any;

  constructor() {
    super();
    this.$spans = new FormatSpans();
    this.$defaultFmt = defaultFmt();
    this.$bounds = { xMin: 0, yMin: 0, xMax: 100 * TWIPS, yMax: 100 * TWIPS };
    this.$type = 'dynamic';
    this.$selectable = true;
    this.$multiline = false;
    this.$wordWrap = false;
    this.$autoSize = 'none';
    this.$border = false;
    this.$borderColor = 0;
    this.$background = false;
    this.$backgroundColor = 0xffffff;
    this.$embedFonts = false;
    this.$maxChars = 0;
    this.$restrict = null;
    this.$restrictFn = null;
    this.$password = false;
    this.$antiAliasType = 'normal';
    this.$condenseWhite = false;
    this.$scrollV = 1;
    this.$scrollH = 0;
    this.$selBegin = 0;
    this.$selEnd = 0;
    this.$layout = null;
    this.$wasStatic = false;
    this.$htmlFlag = false;
    this.$variable = null;
    this.mouseWheelEnabled = true;
    this.alwaysShowSelection = false;
    this.gridFitType = 'pixel';
    this.sharpness = 0;
    this.thickness = 0;
    this.useRichTextClipboard = false;
    this.styleSheet = null;
  }

  /** @internal Initialise from a manifest EditTextDef (timeline / library text fields). */
  $initFromDef(def: EditTextDef, font?: { name: string; bold: boolean; italic: boolean }): void {
    this.$bounds = {
      xMin: Math.round(def.bounds.xMin * TWIPS),
      yMin: Math.round(def.bounds.yMin * TWIPS),
      xMax: Math.round(def.bounds.xMax * TWIPS),
      yMax: Math.round(def.bounds.yMax * TWIPS),
    };
    const f = defaultFmt();
    // Flash exposes the referenced font's own name and style (DefineFont flags) in TextFormat;
    // the CSS family (`fontName`) is only a rendering alias (see AssetLibrary.resolveFont).
    if (font) {
      f.font = font.name;
      f.bold = font.bold;
      f.italic = font.italic;
    } else if (def.fontName) f.font = def.fontName;
    else if (def.fontClass) f.font = def.fontClass;
    if (def.size !== undefined) f.size = def.size;
    if (def.color !== undefined) f.color = def.color & 0xffffff;
    if (def.align) f.align = def.align;
    if (def.leftMargin !== undefined) f.leftMargin = def.leftMargin;
    if (def.rightMargin !== undefined) f.rightMargin = def.rightMargin;
    if (def.indent !== undefined) f.indent = def.indent;
    if (def.leading !== undefined) f.leading = def.leading;
    this.$defaultFmt = f;
    this.$wordWrap = !!def.wordWrap;
    this.$multiline = !!def.multiline;
    this.$password = !!def.password;
    if (this.$password) this.$sensitive = true;
    this.$type = def.readOnly ? 'dynamic' : 'input';
    this.$selectable = !def.noSelect;
    this.$border = !!def.border;
    this.$background = !!def.border;
    this.$htmlFlag = !!def.html;
    this.$embedFonts = !!def.useOutlines;
    this.$autoSize = def.autoSize ? 'left' : 'none';
    this.$maxChars = def.maxLength ?? 0;
    this.$wasStatic = !!def.wasStatic;
    this.$variable = def.variableName ?? null;
    const init = localized(def.initialText ?? '');
    if (def.html) this.$spans = parseHtml(init, this.$defaultFmt, this.$multiline);
    else this.$spans = FormatSpans.fromText(normalizeNewlines(init), this.$defaultFmt);
    this.$dirty();
  }

  /** @internal */ get $renderKind(): RenderKind {
    return 'text';
  }

  /**
   * @internal PORT: the text last set by `text` / `htmlText` (with the default format then), while nothing else has
   * changed the field since: the same text set again (a HUD writing its value every frame) changes nothing.
   */
  $lastSet: string | null = null;
  /** @internal */ $lastSetFmt: Fmt | null = null;

  /** @internal Layout invalidation. */
  $dirty(): void {
    this.$layout = null;
    this.$lastSet = null;
    this.$invalidate();
  }

  /** @internal Compute layout (and apply autoSize to the bounds) if needed. */
  $ensureLayout(): TextLayout {
    if (this.$layout) return this.$layout;
    const b = this.$bounds;
    const autosize = this.$autoSize !== 'none';
    const contentW = !autosize || this.$wordWrap ? Math.max(0, (b.xMax - b.xMin) / TWIPS - 2 * GUTTER) : null;
    const layout = layoutText(this.$spans, this.$defaultFmt, {
      width: contentW,
      wordWrap: this.$wordWrap,
      password: this.$password,
      isInput: this.$type === 'input',
      sensitive: this.$sensitive,
    });
    this.$layout = layout;
    if (autosize) {
      if (!this.$wordWrap) {
        let w = toTwips(layout.textWidth) + 2 * GUTTER * TWIPS;
        if (this.$type === 'input') w += toTwips(2.5);
        let x0 = b.xMin;
        if (this.$autoSize === 'center') x0 = Math.trunc((b.xMin + b.xMax - w) / 2);
        else if (this.$autoSize === 'right') x0 = b.xMax - w;
        b.xMin = x0;
        b.xMax = x0 + w;
      }
      b.yMax = b.yMin + toTwips(layout.textHeight) + 2 * GUTTER * TWIPS;
    }
    // Clamp scroll after relayout.
    if (this.$scrollV > this.maxScrollV) this.$scrollV = this.maxScrollV;
    return layout;
  }

  // ------------------------------------------------------------------ geometry overrides
  $selfBounds(): TRect {
    this.$ensureLayout();
    return { ...this.$bounds };
  }

  get x(): number {
    this.$ensureLayout();
    return (this.$m.tx + toTwips(this.scaleX * (this.$bounds.xMin / TWIPS))) / TWIPS;
  }
  set x(v: number) {
    this.$ensureLayout();
    const off = toTwips(this.scaleX * (this.$bounds.xMin / TWIPS));
    const t = (toTwips(+v) - off) | 0;
    this.$transformedByScript = true;
    if (this.$m.tx === t) return;
    this.$m.tx = t;
    this.$invalidate();
  }
  get y(): number {
    this.$ensureLayout();
    return (this.$m.ty + toTwips(this.scaleY * (this.$bounds.yMin / TWIPS))) / TWIPS;
  }
  set y(v: number) {
    this.$ensureLayout();
    const off = toTwips(this.scaleY * (this.$bounds.yMin / TWIPS));
    const t = (toTwips(+v) - off) | 0;
    this.$transformedByScript = true;
    if (this.$m.ty === t) return;
    this.$m.ty = t;
    this.$invalidate();
  }
  get width(): number {
    this.$ensureLayout();
    const r = tmatRect(this.$m, this.$bounds);
    return rectValid(r) ? (r.xMax - r.xMin) / TWIPS : 0;
  }
  set width(v: number) {
    v = +v;
    if (!(v >= 0)) return;
    this.$ensureLayout();
    this.$bounds.xMax = this.$bounds.xMin + toTwips(v);
    this.$transformedByScript = true;
    this.$dirty();
  }
  get height(): number {
    this.$ensureLayout();
    const r = tmatRect(this.$m, this.$bounds);
    return rectValid(r) ? (r.yMax - r.yMin) / TWIPS : 0;
  }
  set height(v: number) {
    v = +v;
    if (!(v >= 0)) return;
    this.$ensureLayout();
    this.$bounds.yMax = this.$bounds.yMin + toTwips(v);
    this.$transformedByScript = true;
    this.$dirty();
  }

  $hitTestLocalContent(): boolean {
    return true;
  }

  $mousePick(gx: number, gy: number): MousePick {
    if (!this.$visible) return PICK_MISS;
    const wb = this.$worldBounds();
    if (!rectValid(wb) || !rectContains(wb, gx, gy)) return PICK_MISS;
    if (this.$mask && !this.$mask.$hitTestShape(gx, gy, 0)) return PICK_MISS;
    if (this.$mouseEnabled && (this.$selectable || !this.$wasStatic)) return { kind: 'hit', target: this };
    return PICK_PROPAGATE;
  }

  // ------------------------------------------------------------------ text API
  get text(): string {
    return this.$spans.text;
  }
  set text(v: string) {
    const s = normalizeNewlines(localized(v === null || v === undefined ? '' : String(v)));
    const key = 't' + s;
    if (this.$sameSet(key)) return;
    this.$spans = FormatSpans.fromText(s, this.$defaultFmt);
    this.$afterProgrammaticChange();
    this.$lastSet = key;
    this.$lastSetFmt = this.$defaultFmt;
  }

  /** @internal The field already shows what setting `key` would give (text, formats, scroll at the top). */
  $sameSet(key: string): boolean {
    return this.$lastSet === key && this.$lastSetFmt === this.$defaultFmt && this.$scrollV === 1 && this.$scrollH === 0;
  }

  get htmlText(): string {
    return toHtml(this.$spans);
  }
  set htmlText(v: string) {
    const s = localized(v === null || v === undefined ? '' : String(v));
    const key = `h${this.$condenseWhite ? 1 : 0}${this.$multiline ? 1 : 0}${s}`;
    if (this.$sameSet(key)) return;
    this.$spans = parseHtml(this.$condenseWhite ? s.replace(/\s+/g, ' ') : s, this.$defaultFmt, this.$multiline);
    this.$afterProgrammaticChange();
    this.$lastSet = key;
    this.$lastSetFmt = this.$defaultFmt;
  }

  /** @internal */ $afterProgrammaticChange(): void {
    const len = this.$spans.text.length;
    if (this.$selBegin > len) this.$selBegin = len;
    if (this.$selEnd > len) this.$selEnd = len;
    this.$scrollV = 1;
    this.$scrollH = 0;
    this.$dirty();
  }

  get length(): number {
    return this.$spans.text.length;
  }

  appendText(s: string): void {
    const len = this.$spans.text.length;
    this.replaceText(len, len, s);
  }

  replaceText(begin: number, end: number, s: string): void {
    begin = begin | 0;
    end = end | 0;
    const len = this.$spans.text.length;
    if (begin < 0 || end < begin || end > len) throw rangeError();
    const fmt = (begin > 0 ? this.$spans.fmtAt(begin - 1) : this.$spans.fmtAt(begin)) ?? this.$defaultFmt;
    this.$spans.replace(begin, end, normalizeNewlines(String(s)), fmt);
    this.$dirty();
  }

  get textColor(): number {
    const f = this.$spans.text.length ? this.$spans.fmtAt(0) : null;
    return (f ?? this.$defaultFmt).color;
  }
  set textColor(c: number) {
    c = (c >>> 0) & 0xffffff;
    const tf = new TextFormat();
    tf.color = c;
    if (this.$spans.text.length) this.$spans.setFormat(0, this.$spans.text.length, tf);
    this.$defaultFmt = cloneFmt(this.$defaultFmt);
    this.$defaultFmt.color = c;
    this.$dirty();
  }

  get defaultTextFormat(): TextFormat {
    return fmtToTextFormat(this.$defaultFmt);
  }
  set defaultTextFormat(tf: TextFormat) {
    if (!tf) throw new TypeError('Error #2007: Parameter format must be non-null.');
    this.$defaultFmt = applyTextFormat(this.$defaultFmt, tf);
  }

  setTextFormat(format: TextFormat, beginIndex: number = -1, endIndex: number = -1): void {
    if (!format) throw new TypeError('Error #2007: Parameter format must be non-null.');
    const len = this.$spans.text.length;
    let b = beginIndex | 0;
    let e = endIndex | 0;
    if (b === -1 && e === -1) {
      b = 0;
      e = len;
    } else if (e === -1) e = b + 1;
    if (b < 0 || b > len || e > len || e < b) throw rangeError();
    this.$spans.setFormat(b, e, format);
    this.$dirty();
  }

  getTextFormat(beginIndex: number = -1, endIndex: number = -1): TextFormat {
    const len = this.$spans.text.length;
    let b = beginIndex | 0;
    let e = endIndex | 0;
    if (b === -1 && e === -1) {
      b = 0;
      e = len;
    } else if (e === -1) e = b + 1;
    if (b < 0 || e > len || e < b || (len > 0 && b >= len && b !== e)) throw rangeError();
    if (len === 0 || b === e) return fmtToTextFormat(this.$spans.fmtAt(Math.max(0, b - 1)) ?? this.$defaultFmt);
    return this.$spans.getFormat(b, e);
  }

  get type(): string {
    return this.$type;
  }
  set type(v: string) {
    if (v !== 'input' && v !== 'dynamic') throw new Error('ArgumentError: Error #2008: Parameter type must be one of the accepted values.');
    this.$type = v;
    this.$dirty();
  }
  get selectable(): boolean {
    return this.$selectable;
  }
  set selectable(v: boolean) {
    this.$selectable = !!v;
  }
  get multiline(): boolean {
    return this.$multiline;
  }
  set multiline(v: boolean) {
    this.$multiline = !!v;
  }
  get wordWrap(): boolean {
    return this.$wordWrap;
  }
  set wordWrap(v: boolean) {
    this.$wordWrap = !!v;
    this.$dirty();
  }
  get autoSize(): string {
    return this.$autoSize;
  }
  set autoSize(v: string) {
    if (v !== 'none' && v !== 'left' && v !== 'center' && v !== 'right') {
      throw new Error('ArgumentError: Error #2008: Parameter autoSize must be one of the accepted values.');
    }
    this.$autoSize = v;
    this.$dirty();
  }
  get border(): boolean {
    return this.$border;
  }
  set border(v: boolean) {
    this.$border = !!v;
    this.$invalidate();
  }
  get borderColor(): number {
    return this.$borderColor;
  }
  set borderColor(v: number) {
    this.$borderColor = (v >>> 0) & 0xffffff;
    this.$invalidate();
  }
  get background(): boolean {
    return this.$background;
  }
  set background(v: boolean) {
    this.$background = !!v;
    this.$invalidate();
  }
  get backgroundColor(): number {
    return this.$backgroundColor;
  }
  set backgroundColor(v: number) {
    this.$backgroundColor = (v >>> 0) & 0xffffff;
    this.$invalidate();
  }
  get embedFonts(): boolean {
    return this.$embedFonts;
  }
  set embedFonts(v: boolean) {
    this.$embedFonts = !!v;
    this.$dirty();
  }
  get maxChars(): number {
    return this.$maxChars;
  }
  set maxChars(v: number) {
    this.$maxChars = Math.max(0, v | 0);
  }
  get restrict(): string | null {
    return this.$restrict;
  }
  set restrict(v: string | null) {
    this.$restrict = v === null || v === undefined ? null : String(v);
    this.$restrictFn = compileRestrict(this.$restrict);
  }
  get displayAsPassword(): boolean {
    return this.$password;
  }
  set displayAsPassword(v: boolean) {
    this.$password = !!v;
    // PORT: once a password field, always sensitive (see $sensitive), also while a "show" toggle turns the mask off.
    if (this.$password) this.$sensitive = true;
    this.$dirty();
  }
  get antiAliasType(): string {
    return this.$antiAliasType;
  }
  set antiAliasType(v: string) {
    this.$antiAliasType = String(v);
  }
  get condenseWhite(): boolean {
    return this.$condenseWhite;
  }
  set condenseWhite(v: boolean) {
    this.$condenseWhite = !!v;
  }

  get textWidth(): number {
    return this.$ensureLayout().textWidth;
  }
  get textHeight(): number {
    return this.$ensureLayout().textHeight;
  }
  get numLines(): number {
    return Math.max(1, this.$ensureLayout().lines.length);
  }

  get scrollV(): number {
    this.$ensureLayout();
    return this.$scrollV;
  }
  set scrollV(v: number) {
    const max = this.maxScrollV;
    const nv = Math.min(Math.max(1, v | 0), max);
    if (nv !== this.$scrollV) {
      this.$scrollV = nv;
      this.$invalidate();
      this.$dispatch(new Event(Event.SCROLL, false, false));
    }
  }
  get maxScrollV(): number {
    const layout = this.$ensureLayout();
    const lines = layout.lines;
    if (!lines.length) return 1;
    const windowH = (this.$bounds.yMax - this.$bounds.yMin) / TWIPS - 2 * GUTTER;
    const target = layout.textHeight - windowH;
    for (const l of lines) if (l.y >= target - 1e-9) return l.index + 1;
    return lines[lines.length - 1].index + 1;
  }
  get bottomScrollV(): number {
    const layout = this.$ensureLayout();
    const lines = layout.lines;
    if (!lines.length) return 1;
    const off = lines[this.$scrollV - 1]?.y ?? 0;
    const target = (this.$bounds.yMax - this.$bounds.yMin) / TWIPS + off - 2 * GUTTER;
    for (const l of lines) if (l.y + l.ascent + l.descent > target) return Math.max(1, l.index);
    return lines[lines.length - 1].index + 1;
  }
  get scrollH(): number {
    return this.$scrollH;
  }
  set scrollH(v: number) {
    this.$scrollH = Math.max(0, Math.min(v | 0, this.maxScrollH));
    this.$invalidate();
  }
  get maxScrollH(): number {
    if (this.$wordWrap) return 0;
    const layout = this.$ensureLayout();
    const windowW = Math.max(0, (this.$bounds.xMax - this.$bounds.xMin) / TWIPS - 2 * GUTTER);
    let tw = layout.textWidth;
    if (this.$type === 'input') tw += windowW / 4;
    return Math.max(0, Math.trunc(tw - windowW));
  }

  getLineText(lineIndex: number): string {
    const lines = this.$ensureLayout().lines;
    lineIndex = lineIndex | 0;
    if (lineIndex < 0 || lineIndex >= lines.length) throw rangeError();
    const start = lines[lineIndex].start;
    const end = lineIndex + 1 < lines.length ? lines[lineIndex + 1].start : this.$spans.text.length;
    return this.$spans.text.slice(start, end);
  }
  getLineLength(lineIndex: number): number {
    return this.getLineText(lineIndex).length;
  }
  getLineOffset(lineIndex: number): number {
    const lines = this.$ensureLayout().lines;
    lineIndex = lineIndex | 0;
    if (lineIndex < 0 || lineIndex >= lines.length) throw rangeError();
    return lines[lineIndex].start;
  }
  getLineIndexOfChar(charIndex: number): number {
    const lines = this.$ensureLayout().lines;
    for (let i = 0; i < lines.length; i++) {
      const end = i + 1 < lines.length ? lines[i + 1].start : this.$spans.text.length;
      if (charIndex >= lines[i].start && charIndex < end) return i;
    }
    return -1;
  }
  getLineMetrics(lineIndex: number): { x: number; width: number; height: number; ascent: number; descent: number; leading: number } {
    const lines = this.$ensureLayout().lines;
    lineIndex = lineIndex | 0;
    if (lineIndex < 0 || lineIndex >= lines.length) throw rangeError();
    const l = lines[lineIndex];
    return { x: l.x + GUTTER, width: l.width, height: l.ascent + l.descent + l.leading, ascent: l.ascent, descent: l.descent, leading: l.leading };
  }
  getCharBoundaries(charIndex: number): Rectangle | null {
    const layout = this.$ensureLayout();
    for (const l of layout.lines) {
      for (const r of l.runs) {
        if (charIndex >= r.start && charIndex < r.end) {
          const pre = r.text.slice(0, charIndex - r.start);
          const ch = r.text.slice(charIndex - r.start, charIndex - r.start + 1);
          const x0 = r.x + measureRun(pre, r.fmt, r.noCache);
          return new Rectangle(x0 + GUTTER, l.y + GUTTER, measureRun(ch, r.fmt, r.noCache), l.ascent + l.descent);
        }
      }
    }
    return null;
  }
  getCharIndexAtPoint(x: number, y: number): number {
    const layout = this.$ensureLayout();
    const lx = x - GUTTER;
    const ly = y - GUTTER + (layout.lines[this.$scrollV - 1]?.y ?? 0);
    for (const l of layout.lines) {
      if (ly < l.y || ly >= l.y + l.ascent + l.descent + l.leading) continue;
      for (const r of l.runs) {
        let cx = r.x;
        for (let i = 0; i < r.text.length; i++) {
          const w = measureRun(r.text[i], r.fmt, r.noCache);
          if (lx >= cx && lx < cx + w) return r.start + i;
          cx += w;
        }
      }
      return -1;
    }
    return -1;
  }
  getFirstCharInParagraph(charIndex: number): number {
    const t = this.$spans.text;
    if (charIndex < 0 || charIndex > t.length) return -1;
    return t.lastIndexOf('\r', charIndex - 1) + 1;
  }
  getParagraphLength(charIndex: number): number {
    const t = this.$spans.text;
    const s = this.getFirstCharInParagraph(charIndex);
    if (s < 0) return -1;
    const e = t.indexOf('\r', s);
    return (e < 0 ? t.length : e + 1) - s;
  }

  get selectionBeginIndex(): number {
    return Math.min(this.$selBegin, this.$selEnd);
  }
  get selectionEndIndex(): number {
    return Math.max(this.$selBegin, this.$selEnd);
  }
  get caretIndex(): number {
    return this.$selEnd;
  }
  setSelection(begin: number, end: number): void {
    const len = this.$spans.text.length;
    this.$selBegin = Math.max(0, Math.min(begin | 0, len));
    this.$selEnd = Math.max(0, Math.min(end | 0, len));
    this.$invalidate();
  }
  replaceSelectedText(s: string): void {
    const a = this.selectionBeginIndex;
    const b = this.selectionEndIndex;
    this.replaceText(a, b, s);
    this.$selBegin = this.$selEnd = a + String(s).length;
  }

  // ------------------------------------------------------------------ keyboard input (focused input field)
  /** @internal Insert user-typed text (TEXT_INPUT is dispatched first and may be cancelled). */
  $userInsert(str: string): void {
    if (this.$type !== 'input') return;
    const ev = new TextEvent(TextEvent.TEXT_INPUT, true, true, str);
    this.$dispatch(ev);
    if (ev.isDefaultPrevented()) return;
    let s = str;
    let a = this.selectionBeginIndex;
    const b = this.selectionEndIndex;
    if (this.$restrictFn) {
      // PORT: a restricted field sees composed characters (NFC) before its filter: a keyboard or IME that sends a
      // letter and its combining mark ("и" + U+0306) gets the allowed "й" (what the shared nickname rules read), not
      // the bare letter with the mark dropped. A mark that arrives on its own composes with the character before the
      // caret.
      s = s.normalize('NFC');
      if (a === b && a > 0 && /^\p{M}/u.test(s)) {
        const prev = this.$spans.text.charAt(a - 1);
        const joined = (prev + s).normalize('NFC');
        // Only a composition the filter accepts replaces the previous character (else the mark is just dropped).
        if (joined.length < prev.length + s.length && this.$restrictFn(Array.from(joined)[0])) {
          a -= 1;
          s = joined;
        }
      }
      // A composed character the filter refuses keeps what of it the filter allows (its base letter), as before.
      const allowed = (c: string) => c === '\r' || this.$restrictFn!(c);
      s = Array.from(s)
        .map((c) => (allowed(c) ? c : Array.from(c.normalize('NFD')).filter(allowed).join('')))
        .join('');
    }
    if (this.$maxChars > 0) {
      const room = this.$maxChars - (this.$spans.text.length - (b - a));
      if (room <= 0) s = '';
      else s = s.slice(0, insidePair(s, room) ? room - 1 : room);
    }
    if (!s.length && a === b) return;
    const fmt = (a > 0 ? this.$spans.fmtAt(a - 1) : this.$spans.fmtAt(a)) ?? this.$defaultFmt;
    this.$spans.replace(a, b, s, fmt);
    this.$selBegin = this.$selEnd = a + s.length;
    this.$dirty();
    this.$scrollToCaret();
    this.$dispatch(new Event(Event.CHANGE, true, false));
  }

  /**
   * @internal Keeps the caret of an input field in view: Flash scrolls a single-line field horizontally while the
   * player types past its right edge (a long password) or moves the caret back, and a multiline one vertically to the
   * caret's line.
   */
  $scrollToCaret(): void {
    if (this.$type !== 'input') return;
    if (this.$multiline || this.$wordWrap) {
      const lines = this.$ensureLayout().lines;
      if (!lines.length) return;
      const idx = this.$selEnd;
      let li = lines.length - 1;
      for (let i = 0; i + 1 < lines.length; i++) {
        if (idx < lines[i + 1].start) {
          li = i;
          break;
        }
      }
      const line = li + 1;
      const top = this.scrollV;
      const bottom = this.bottomScrollV;
      if (line < top) this.scrollV = line;
      else if (line > bottom) this.scrollV = top + (line - bottom);
      return;
    }
    const line = this.$ensureLayout().lines[0];
    if (!line) return;
    const idx = this.$selEnd;
    let x = line.runs.length ? line.runs[line.runs.length - 1].x + line.runs[line.runs.length - 1].width : 0;
    for (const r of line.runs) {
      if (idx < r.start || idx > r.end) continue;
      x = r.x + measureRun(r.text.slice(0, idx - r.start), r.fmt, r.noCache);
      break;
    }
    const windowW = Math.max(0, (this.$bounds.xMax - this.$bounds.xMin) / TWIPS - 2 * GUTTER);
    let next = this.$scrollH;
    if (x - next > windowW - 1) next = Math.ceil(x - windowW + 1);
    else if (x < next) next = Math.floor(x);
    next = Math.max(0, Math.min(next, this.maxScrollH));
    if (next !== this.$scrollH) {
      this.$scrollH = next;
      this.$invalidate();
    }
  }

  /** @internal Handle an editing key on the focused input field. Returns true if consumed. */
  $handleKey(keyCode: number, charCode: number, mods: { shift?: boolean; ctrl?: boolean; alt?: boolean }): boolean {
    const handled = this.$editKey(keyCode, charCode, mods);
    if (handled) this.$scrollToCaret();
    return handled;
  }

  private $editKey(keyCode: number, charCode: number, mods: { shift?: boolean; ctrl?: boolean; alt?: boolean }): boolean {
    if (this.$type !== 'input') {
      return false;
    }
    const text = this.$spans.text;
    const len = text.length;
    const a = this.selectionBeginIndex;
    const b = this.selectionEndIndex;
    // One character back / forward: a surrogate pair is one character (insidePair).
    const back = (i: number) => Math.max(0, insidePair(text, i - 1) ? i - 2 : i - 1);
    const forward = (i: number) => Math.min(len, insidePair(text, i + 1) ? i + 2 : i + 1);
    const del = (from: number, to: number) => {
      if (from >= to) return;
      this.$spans.replace(from, to, '', this.$defaultFmt);
      this.$selBegin = this.$selEnd = from;
      this.$dirty();
      this.$dispatch(new Event(Event.CHANGE, true, false));
    };
    switch (keyCode) {
      case 8: // Backspace
        if (a !== b) del(a, b);
        else if (a > 0) del(back(a), a);
        return true;
      case 46: // Delete
        if (a !== b) del(a, b);
        else if (a < len) del(a, forward(a));
        return true;
      case 37: // Left
        this.$selBegin = this.$selEnd = mods.shift ? this.$selEnd : a === b ? back(a) : a;
        if (mods.shift) this.$selEnd = back(this.$selEnd);
        this.$invalidate();
        return true;
      case 39: // Right
        if (mods.shift) this.$selEnd = forward(this.$selEnd);
        else this.$selBegin = this.$selEnd = a === b ? forward(b) : b;
        this.$invalidate();
        return true;
      case 36: // Home
        this.$selBegin = this.$selEnd = 0;
        this.$invalidate();
        return true;
      case 35: // End
        this.$selBegin = this.$selEnd = len;
        this.$invalidate();
        return true;
      case 13: // Enter
        if (this.$multiline) this.$userInsert('\r');
        return true;
      default:
        break;
    }
    if (mods.ctrl && (keyCode === 65 || charCode === 1)) {
      this.$selBegin = 0;
      this.$selEnd = len;
      this.$invalidate();
      return true;
    }
    if (charCode >= 32 && charCode !== 127 && !mods.ctrl) {
      this.$userInsert(String.fromCharCode(charCode));
      return true;
    }
    return false;
  }

  // ------------------------------------------------------------------ renderer seam
  /**
   * Render data for the renderer: bounds (px, local), decorations, visible lines already offset by
   * the vertical scroll, and caret/selection when focused. Text coordinates are local to the
   * field; line y is the top of the line box, baseline = y + ascent.
   */
  $renderInfo(): {
    bounds: { x: number; y: number; width: number; height: number };
    border: boolean;
    borderColor: number;
    background: boolean;
    backgroundColor: number;
    embedFonts: boolean;
    lines: TextLayout['lines'];
    offsetX: number;
    offsetY: number;
    firstLine: number;
    focused: boolean;
    selection: [number, number];
    type: string;
  } {
    const layout = this.$ensureLayout();
    const b = this.$bounds;
    const scrollY = layout.lines[this.$scrollV - 1]?.y ?? 0;
    const st: any = this.stage;
    return {
      bounds: { x: b.xMin / TWIPS, y: b.yMin / TWIPS, width: (b.xMax - b.xMin) / TWIPS, height: (b.yMax - b.yMin) / TWIPS },
      border: this.$border,
      borderColor: this.$borderColor,
      background: this.$background,
      backgroundColor: this.$backgroundColor,
      embedFonts: this.$embedFonts,
      lines: layout.lines,
      offsetX: b.xMin / TWIPS + GUTTER - this.$scrollH,
      offsetY: b.yMin / TWIPS + GUTTER - scrollY,
      firstLine: this.$scrollV - 1,
      focused: !!st && st.$focus === this,
      selection: [this.selectionBeginIndex, this.selectionEndIndex],
      type: this.$type,
    };
  }
}

function measureRun(text: string, f: Fmt, noCache = false): number {
  if (!text.length) return 0;
  return _measureText(text, { family: f.font, size: f.size, bold: f.bold, italic: f.italic }, noCache) + f.letterSpacing * text.length;
}

export function normalizeNewlines(s: string): string {
  return s.replace(/\r\n|\n/g, '\r');
}
